import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, ArrowLeft, MagnifyingGlass, Seal, CookingPot, MapPin } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusIcons = {
  pending: Clock,
  confirmed: Seal,
  preparing: CookingPot,
  shipping: Truck,
  delivered: CheckCircle,
};

const TrackingPage = () => {
  const { trackingCode: paramCode } = useParams();
  const [trackingCode, setTrackingCode] = useState(paramCode || '');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(!!paramCode);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(!!paramCode);

  const fetchTracking = async (code) => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const resp = await axios.get(`${API_URL}/api/tracking/${code.trim()}`);
      setTracking(resp.data);
    } catch {
      setError('Commande introuvable. Vérifiez votre code de suivi.');
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paramCode) fetchTracking(paramCode);
  }, [paramCode]); // eslint-disable-line

  const primaryColor = tracking?.store_primary_color || '#c2185b';
  const statusFlow = tracking?.status_flow || ['pending', 'confirmed', 'preparing', 'shipping', 'delivered'];
  const statusLabels = tracking?.status_labels || {};
  const currentIdx = statusFlow.indexOf(tracking?.status);

  return (
    <div className="min-h-screen bg-[#f5f5f5]" data-testid="tracking-page">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-6">
        <div className="max-w-[700px] mx-auto px-4 flex items-center gap-4">
          <Link to="/" className="text-white/60 hover:text-white transition-colors" data-testid="tracking-back-home">
            <ArrowLeft size={20} />
          </Link>
          {tracking?.store_logo && <img src={tracking.store_logo} alt="" className="h-10 w-10 rounded-full bg-white p-0.5" />}
          <div>
            <h1 className="text-white text-lg font-bold">{tracking?.store_name || 'Suivi de commande'}</h1>
            <p className="text-white/50 text-xs">Suivez votre commande en temps réel</p>
          </div>
        </div>
      </div>

      <div className="max-w-[700px] mx-auto px-4 py-8">
        {/* Search box */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" data-testid="tracking-search">
          <label className="text-sm font-semibold text-slate-700 mb-2 block">Code de suivi</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={trackingCode}
                onChange={e => setTrackingCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && fetchTracking(trackingCode)}
                placeholder="Ex: MGRE-A1B2C3"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 font-mono tracking-wider"
                style={{ '--tw-ring-color': primaryColor }}
                data-testid="tracking-input"
              />
            </div>
            <button
              onClick={() => fetchTracking(trackingCode)}
              className="px-6 py-3 text-white rounded-xl text-sm font-semibold transition-all hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
              data-testid="tracking-search-btn"
            >
              Suivre
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: primaryColor }} />
            <p className="text-slate-500 text-sm mt-3">Recherche en cours...</p>
          </div>
        )}

        {/* Error */}
        {error && searched && !loading && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="tracking-error">
            <Package size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">{error}</p>
          </div>
        )}

        {/* Result */}
        {tracking && !loading && (
          <div className="space-y-5" data-testid="tracking-result">
            {/* Order summary */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold" data-testid="tracking-order-number">Commande {tracking.order_number}</h2>
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="tracking-status-badge"
                >
                  {tracking.status_label}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-3">
                <span>Code : <strong className="font-mono text-slate-700">{tracking.tracking_code}</strong></span>
                <span>{tracking.items_count} article(s)</span>
                <span className="font-semibold text-slate-700">{tracking.total?.toFixed(2)} EUR</span>
                {tracking.shipping_address?.city && (
                  <span className="flex items-center gap-1"><MapPin size={12} /> {tracking.shipping_address.postal_code} {tracking.shipping_address.city}</span>
                )}
              </div>
            </div>

            {/* Status progression */}
            <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="tracking-progression">
              <h3 className="text-sm font-bold text-slate-700 mb-6">Progression</h3>
              <div className="flex items-center justify-between relative">
                {/* Progress bar background */}
                <div className="absolute top-5 left-[10%] right-[10%] h-1 bg-slate-200 rounded-full" />
                {/* Progress bar filled */}
                <div
                  className="absolute top-5 left-[10%] h-1 rounded-full transition-all duration-700"
                  style={{
                    backgroundColor: primaryColor,
                    width: `${currentIdx >= 0 ? (currentIdx / (statusFlow.length - 1)) * 80 : 0}%`,
                  }}
                />
                {statusFlow.map((st, i) => {
                  const Icon = statusIcons[st] || Package;
                  const isActive = i <= currentIdx;
                  const isCurrent = i === currentIdx;
                  return (
                    <div key={st} className="relative flex flex-col items-center z-10" style={{ width: `${100 / statusFlow.length}%` }}>
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${isCurrent ? 'ring-4 ring-opacity-30 scale-110' : ''}`}
                        style={{
                          backgroundColor: isActive ? primaryColor : '#e2e8f0',
                          color: isActive ? '#fff' : '#94a3b8',
                          '--tw-ring-color': primaryColor,
                        }}
                        data-testid={`tracking-step-${st}`}
                      >
                        <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                      </div>
                      <span className={`text-[11px] mt-2 text-center leading-tight ${isActive ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>
                        {statusLabels[st] || st}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* History timeline */}
            {tracking.history?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="tracking-timeline">
                <h3 className="text-sm font-bold text-slate-700 mb-4">Historique</h3>
                <div className="relative ml-2">
                  <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-slate-200" />
                  {[...tracking.history].reverse().map((entry, i) => {
                    const Icon = statusIcons[entry.status] || Package;
                    return (
                      <div key={i} className="relative pl-10 pb-5 last:pb-0" data-testid={`tracking-history-${i}`}>
                        <div
                          className="absolute left-0 top-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: i === 0 ? primaryColor : '#e2e8f0', color: i === 0 ? '#fff' : '#94a3b8' }}
                        >
                          <Icon size={11} weight="fill" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800">{statusLabels[entry.status] || entry.status}</span>
                            <span className="text-xs text-slate-400">
                              {new Date(entry.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {entry.notes && <p className="text-xs text-slate-500 mt-0.5">{entry.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tracking number */}
            {tracking.tracking_number && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-sm font-bold text-slate-700 mb-2">Numéro de suivi transporteur</h3>
                <p className="font-mono text-lg tracking-wider" style={{ color: primaryColor }}>{tracking.tracking_number}</p>
              </div>
            )}
          </div>
        )}

        {/* Empty state — no search yet */}
        {!searched && !loading && (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <Truck size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">Entrez votre code de suivi</p>
            <p className="text-slate-400 text-sm mt-1">Le code se trouve dans votre email de confirmation</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingPage;
